import { useState, useCallback } from "react";

import api from "@services/api";
import { useToast } from "@hooks/useToast";
import useTranslation from "src/translations/useTranslation";
import { ToastMessages } from "@shared/languages/interfaces";
import { TravelAnalytics } from "@shared/interfaces/travel-analytics.interface";

/**
 * Hook that fetches coordinate view data for a single travel from the new analysis endpoint.
 * Returns tracker commands enriched with states, alerts, and events.
 */
const useTravelAnalysisCoordinateView = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const [coords, setCoords] = useState<TravelAnalytics[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCoordinateView = useCallback(async (idTravel: string) => {
    setLoading(true);
    setCoords([]);

    try {
      const { data } = await api.post("travels/analysis/coordinate-view", { idTravel });

      if (data.status === "success") {
        setCoords(data.result || []);
      } else {
        addToast({ type: "error", title: t(ToastMessages.error), description: data.message });
      }
    } catch (err) {
      const message = err?.response?.data?.message || t(ToastMessages.connectionNotEstablished);

      addToast({ type: "error", title: t(ToastMessages.error), description: message });
    } finally {
      setLoading(false);
    }
  }, [addToast, t]);

  const reset = useCallback(() => {
    setCoords([]);
    setLoading(false);
  }, []);

  return { coords, loading, fetchCoordinateView, reset };
};

export default useTravelAnalysisCoordinateView;
